import { AsyncJob, AsyncJobStatus, useAsyncJobs } from '@ais-ui/async-jobs';
import React, { useMemo } from 'react';
import { PiCheck, PiClock, PiHourglass, PiWarning } from 'react-icons/pi';
import { Loader2, XIcon } from 'lucide-react';
import {
  DropdownMenuItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';

export const AsyncJobs = () => {
  const { jobs, cancelJob, clearCompletedJobs } = useAsyncJobs();
  const [open, setOpen] = React.useState(false);

  const anyRunningJobs = useMemo(() => {
    return jobs.some((job: AsyncJob) => job.status === 'running');
  }, [jobs]);

  // Function to calculate job duration
  const calculateDuration = (job: AsyncJob) => {
    if (!job.startedAt) return 'Not started';

    const startTime = new Date(job.startedAt);
    const endTime = job.completedAt ? new Date(job.completedAt) : new Date();

    const durationMs = endTime.getTime() - startTime.getTime();

    // Format duration nicely
    if (durationMs < 1000) {
      return `${durationMs}ms`;
    } else if (durationMs < 60000) {
      return `${Math.floor(durationMs / 1000)}s`;
    } else {
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  };

  // Function to get job status icon
  const getStatusIcon = (status: AsyncJobStatus) => {
    switch (status) {
      case 'completed':
        return <PiCheck className="h-3 w-3 text-green-500" />;
      case 'running':
        return (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        );
      case 'failed':
        return <PiWarning className="h-3 w-3 text-red-500" />;
      default:
        return <PiClock className="h-3 w-3 text-muted-foreground" />;
    }
  };

  const formatJobName = (job: AsyncJob) => {
    return job.name || `Job ${job.id.substring(0, 8)}`;
  };

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <DropdownMenuItem
          id="sidebar-jobs-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(!open);
          }}
          className="w-full h-full flex items-center group/btn space-x-1 hover:bg-secondary"
        >
          <PiHourglass
            className={cn('size-4', anyRunningJobs && 'animate-spin')}
          />
          <p>Jobs {jobs.length > 0 && `(${jobs.length})`}</p>
        </DropdownMenuItem>
      </PopoverTrigger>
      <PopoverContent side="right" className="ml-2" align="end">
        <div className="flex flex-col gap-2">
          {jobs.length > 0 && (
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">Jobs ({jobs.length})</h3>
              <button
                onClick={() => clearCompletedJobs()}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear completed
              </button>
            </div>
          )}

          {jobs.map((job) => (
            <div
              key={job.id}
              className="mb-2 rounded-md p-2 hover:bg-muted/50 border border-muted"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {formatJobName(job)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Duration: {calculateDuration(job)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon(job.status)}
                  {job.status !== 'completed' && job.status !== 'failed' && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        cancelJob(job.id);
                      }}
                      className="ml-1 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                      <XIcon className="h-3 w-3" />
                      <span className="sr-only">Cancel</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {jobs.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">
              No active jobs
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
